import Phaser from 'phaser';
import { Entity, EntityConfig } from './Entity';
import { PathingSystem } from '../systems/PathingSystem';
import { COLORS, DEPTH } from '../constants';
import { TowerType } from './Tower';
import { AudioRouter } from '../systems/AudioRouter';

// ============================================
// Monster Entity - Full Implementation
// Based on towers_monsters_paths_design.md
// ============================================

export type MonsterType =
  // Tier 1 - Early Game
  | 'crawler'     // Ground, basic
  | 'runner'      // Ground, fast
  // Tier 2 - Mid Game
  | 'brute'       // Ground, tank
  | 'shielded'    // Ground, arrow resist
  // Air Units
  | 'bat'         // Air, fast, low HP
  | 'flyer'       // Air, medium
  | 'swarm'       // Air, very low HP, many
  // Late Game
  | 'juggernaut'  // Ground, very tanky, splash resist
  | 'phased';     // Ground, ignores slow

export type MonsterMovementType = 'ground' | 'air';

export interface MonsterStats {
  hp: number;
  speed: number;        // Pixels per second
  damage: number;       // Damage to base
  reward: number;       // Gems on death
  color: number;
  size: number;
  movementType: MonsterMovementType;
  tier: number;
  /** Optional scale multiplier for image-based sprite (e.g. 1.25 = 25% bigger) */
  imageScaleMultiplier?: number;
  /** Optional ms between animation frames (default 200). e.g. swarm uses 75 for faster flicker */
  animationIntervalMs?: number;
  // Resistances (0-1, where 1 = immune)
  arrowResist?: number;
  splashResist?: number;
  slowImmune?: boolean;
}

const MONSTER_STATS: Record<MonsterType, MonsterStats> = {
  // ============================================
  // Tier 1 - Early Game
  // ============================================
  crawler: {
    hp: 30,      // +20% (was 25)
    speed: 45,
    damage: 1,
    reward: 5,   // +20% (was 4)
    color: 0xff4444,
    size: 18,
    movementType: 'ground',
    tier: 1,
  },
  runner: {
    hp: 18,      // +20% (was 15)
    speed: 80,
    damage: 1,
    reward: 4,   // +20% (was 3)
    color: 0xffff44,
    size: 14,
    movementType: 'ground',
    tier: 1,
  },

  // ============================================
  // Tier 2 - Mid Game
  // ============================================
  brute: {
    hp: 120,     // +20% (was 100)
    speed: 30,
    damage: 2,
    reward: 14,  // +20% (was 12)
    color: 0x8844ff,
    size: 35,  // 25% bigger than 28
    movementType: 'ground',
    tier: 2,
    imageScaleMultiplier: 1.25,
    arrowResist: 0.4,  // 40% resist to arrows (heavy armored)
  },
  shielded: {
    hp: 60,      // +20% (was 50)
    speed: 40,
    damage: 1,
    reward: 10,  // +20% (was 8)
    color: 0x888888,
    size: 24,  // 10% bigger than 22
    movementType: 'ground',
    tier: 2,
    imageScaleMultiplier: 1.1,
    arrowResist: 0.6,  // 60% resist to arrows (shielded)
  },

  // ============================================
  // Air Units
  // ============================================
  bat: {
    hp: 14,      // +20% (was 12)
    speed: 70,
    damage: 1,
    reward: 4,   // +20% (was 3)
    color: 0x444444,
    size: 12,
    movementType: 'air',
    tier: 1,
  },
  flyer: {
    hp: 42,      // +20% (was 35)
    speed: 50,
    damage: 1,
    reward: 7,   // +20% (was 6)
    color: 0x6666ff,
    size: 20,
    movementType: 'air',
    tier: 2,
  },
  swarm: {
    hp: 6,       // +20% (was 5)
    speed: 65,
    damage: 1,
    reward: 2,   // +20% (was 1)
    color: 0x66ff66,
    size: 8,
    movementType: 'air',
    tier: 2,
    animationIntervalMs: 75,  // Fast flicker for swarm
  },

  // ============================================
  // Late Game
  // ============================================
  juggernaut: {
    hp: 300,     // +20% (was 250)
    speed: 20,
    damage: 3,
    reward: 30,  // +20% (was 25)
    color: 0xff00ff,
    size: 45,  // 25% bigger than 36 (like brute)
    movementType: 'ground',
    tier: 3,
    imageScaleMultiplier: 1.25,
    arrowResist: 0.5,   // 50% resist to arrows (heavy armored)
    splashResist: 0.5, // 50% resist to splash damage
  },
  phased: {
    hp: 72,      // +20% (was 60)
    speed: 50,
    damage: 2,
    reward: 18,  // +20% (was 15)
    color: 0x00ffff,
    size: 20,
    movementType: 'ground',
    tier: 3,
    slowImmune: true,
  },
};

export interface MonsterConfig extends Omit<EntityConfig, 'type'> {
  monsterType: MonsterType;
  pathId: string;
  routeId?: string;  // For multi-route paths
}

export class Monster extends Entity {
  public readonly monsterType: MonsterType;
  private stats: MonsterStats;
  private pathId: string;
  private routeId: string;
  private pathProgress: number = 0;  // Distance along path
  private hp: number;
  private maxHp: number;

  // Slow effect
  private slowMultiplier: number = 1;
  private slowEndTime: number = 0;

  // Burn effect
  private burnDamage: number = 0;
  private burnEndTime: number = 0;

  // Visuals
  private body: Phaser.GameObjects.Ellipse | Phaser.GameObjects.Arc | Phaser.GameObjects.Image;
  private shadow: Phaser.GameObjects.Container;
  private hpBar: Phaser.GameObjects.Rectangle;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private resistIcon: Phaser.GameObjects.Text | null = null;
  private useImage: boolean = false;

  // Ghost sprite for see-through visibility (2.5D)
  private ghostSprite: Phaser.GameObjects.Ellipse | Phaser.GameObjects.Arc | Phaser.GameObjects.Image | null = null;

  // Sine wave movement offset (for path variation)
  private sineOffset: number = 0;
  private sineFrequency: number;
  private sineAmplitude: number;
  private sinePhase: number;
  // Animation properties (image-based monsters: brute 4 frames, shielded 3, crawler/runner 2, etc.)
  private animationFrame: number = 0;
  private animationFrameCount: number = 2; // Number of frames (1-based keys: monster-{type}-1 .. N)
  private animationTimer: number = 0;
  private readonly BASE_ANIMATION_INTERVAL_MS: number = 200; // Base 200ms between frames
  private lastMoveDirection: number = 1; // 1 = right, -1 = left

  // Callbacks
  public onReachedBase?: () => void;
  public onDeath?: (reward: number) => void;

  constructor(scene: Phaser.Scene, config: MonsterConfig) {
    super(scene, { ...config, type: 'monster' });

    this.monsterType = config.monsterType;
    this.pathId = config.pathId;
    this.routeId = config.routeId || 'main';
    this.stats = { ...MONSTER_STATS[config.monsterType] };
    this.hp = this.stats.hp;
    this.maxHp = this.stats.hp;

    // Initialize sine wave movement parameters with random variation
    // Each monster gets slightly different movement pattern
    this.sinePhase = Math.random() * Math.PI * 2;  // Random starting phase
    this.sineFrequency = 0.008 + Math.random() * 0.004;  // Varies between 0.008-0.012
    this.sineAmplitude = 8 + Math.random() * 8;  // Varies between 8-16 pixels

    // Create visuals - try to use image if available
    const size = this.stats.size;
    const imageKey1 = `monster-${this.monsterType}-1`;

    if (scene.textures.exists(imageKey1)) {
      // Use image-based visual with animation; count how many frames exist (1, 2, 3, ...)
      this.useImage = true;
      this.animationFrameCount = 1;
      while (scene.textures.exists(`monster-${this.monsterType}-${this.animationFrameCount + 1}`)) {
        this.animationFrameCount++;
      }
      const baseScale = 0.25 * (this.stats.imageScaleMultiplier ?? 1);
      this.body = scene.add.image(0, 0, imageKey1);
      (this.body as Phaser.GameObjects.Image).setScale(baseScale);
    } else {
      // Fallback to placeholder shapes
      // Air units are circles, ground units are ovals
      if (this.stats.movementType === 'air') {
        this.body = scene.add.circle(0, 0, size / 2, this.stats.color) as Phaser.GameObjects.Arc;
        (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, 0xffffff);
      } else {
        this.body = scene.add.ellipse(0, 0, size, size * 0.8, this.stats.color);
        (this.body as Phaser.GameObjects.Ellipse).setStrokeStyle(2, 0x000000);
      }
    }

    // Shadow - fuzzy ellipses at bottom of monster (no ghost, stays with main sprite only)
    const bodyBottomY = this.body instanceof Phaser.GameObjects.Image
      ? (this.body as Phaser.GameObjects.Image).displayHeight / 2
      : (this.body instanceof Phaser.GameObjects.Ellipse ? size * 0.4 : size / 2);
    const isFlying = this.stats.movementType === 'air';
    const isShielded = this.monsterType === 'shielded';
    const shadowY = isFlying ? bodyBottomY + 10 : (isShielded ? bodyBottomY - 14 : bodyBottomY - 8);
    this.shadow = scene.add.container(0, shadowY);
    const shadowAlpha = isFlying ? [0.03, 0.04, 0.05, 0.06, 0.07, 0.09] : [0.06, 0.08, 0.1, 0.12, 0.14, 0.18];
    const shadowLayers = [
      { width: size * 3.2, height: size * 0.8, alpha: shadowAlpha[0] },
      { width: size * 2.9, height: size * 0.72, alpha: shadowAlpha[1] },
      { width: size * 2.6, height: size * 0.65, alpha: shadowAlpha[2] },
      { width: size * 2.3, height: size * 0.58, alpha: shadowAlpha[3] },
      { width: size * 2.0, height: size * 0.5, alpha: shadowAlpha[4] },
      { width: size * 1.5, height: size * 0.38, alpha: shadowAlpha[5] },
    ];
    shadowLayers.forEach(layer => {
      const ellipse = scene.add.ellipse(0, 0, layer.width, layer.height, 0x000000, layer.alpha);
      this.shadow.add(ellipse);
    });

    // HP bar background
    this.hpBarBg = scene.add.rectangle(0, -size * 0.6 - 5, size + 4, 6, 0x000000);
    this.hpBarBg.setOrigin(0.5);

    // HP bar
    this.hpBar = scene.add.rectangle(-size / 2, -size * 0.6 - 5, size, 4, COLORS.DANGER);
    this.hpBar.setOrigin(0, 0.5);

    this.container.add([this.shadow, this.body, this.hpBarBg, this.hpBar]);

    // Add resistance indicator if has any
    if (this.stats.arrowResist || this.stats.splashResist || this.stats.slowImmune) {
      this.createResistIcon();
    }

    // Create ghost sprite for see-through visibility
    this.createGhostSprite();
  }

  /**
   * Create a semi-transparent ghost sprite on a high layer for visibility
   */
  private createGhostSprite(): void {
    const size = this.stats.size;
    const imageKey1 = `monster-${this.monsterType}-1`;
    
    if (this.useImage && this.scene.textures.exists(imageKey1)) {
      const baseScale = 0.25 * (this.stats.imageScaleMultiplier ?? 1);
      this.ghostSprite = this.scene.add.image(this._x, this._y, imageKey1);
      (this.ghostSprite as Phaser.GameObjects.Image).setScale(baseScale);
    } else if (this.stats.movementType === 'air') {
      this.ghostSprite = this.scene.add.circle(this._x, this._y, size / 2, this.stats.color) as Phaser.GameObjects.Arc;
    } else {
      this.ghostSprite = this.scene.add.ellipse(this._x, this._y, size, size * 0.8, this.stats.color);
    }
    
    this.ghostSprite.setAlpha(0.4);
    this.ghostSprite.setDepth(DEPTH.GHOST_LAYER);
  }

  private createResistIcon(): void {
    let icon = '';
    if (this.stats.arrowResist) icon = '🛡';
    else if (this.stats.splashResist) icon = '💥';
    else if (this.stats.slowImmune) icon = '⚡';

    this.resistIcon = this.scene.add.text(this.stats.size / 2, -this.stats.size / 2, icon, {
      fontSize: '10px',
    });
    this.resistIcon.setOrigin(0.5);
    this.container.add(this.resistIcon);
  }

  update(delta: number): void {
    if (!this.active || this.destroyed) return;

    // Process burn damage
    this.processBurn(delta);

    // Check slow effect
    if (Date.now() > this.slowEndTime) {
      this.slowMultiplier = 1;
    }

    // Move along path
    const speed = this.stats.speed * this.slowMultiplier;
    this.pathProgress += (speed * delta) / 1000;

    // Get position from pathing system
    const pos = PathingSystem.getPositionAlongPath(this.pathId, this.pathProgress, this.routeId);

    if (pos) {
      // Calculate sine wave offset perpendicular to path
      this.sineOffset = Math.sin(this.pathProgress * this.sineFrequency + this.sinePhase) * this.sineAmplitude;
      
      // Get path direction to calculate perpendicular offset
      // Look ahead slightly to get direction
      const lookAhead = PathingSystem.getPositionAlongPath(
        this.pathId, 
        this.pathProgress + 5, 
        this.routeId
      );
      
      let offsetX = 0;
      let offsetY = 0;
      
      if (lookAhead) {
        // Calculate path direction
        const dx = lookAhead.x - pos.x;
        const dy = lookAhead.y - pos.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        
        if (length > 0.1) {
          // Perpendicular vector (rotate 90 degrees)
          const perpX = -dy / length;
          const perpY = dx / length;
          
          // Apply sine offset perpendicular to path
          offsetX = perpX * this.sineOffset;
          offsetY = perpY * this.sineOffset;
          
          // Determine horizontal direction for sprite flipping
          // Images face right by default, flip when moving left
          if (dx < -0.1) {
            this.lastMoveDirection = -1; // Moving left
          } else if (dx > 0.1) {
            this.lastMoveDirection = 1;  // Moving right
          }
        }
      }
      
      this.setPosition(pos.x + offsetX, pos.y + offsetY);
      
      // Update animation if using images
      if (this.useImage) {
        this.updateAnimation(delta);
      }

      // Air units float higher (visual offset)
      if (this.stats.movementType === 'air') {
        this.container.y -= 10;
      }

      // Check if reached end of path
      const pathLength = PathingSystem.getPathLength(this.pathId, this.routeId);
      if (this.pathProgress >= pathLength) {
        this.reachedBase();
        return;
      }
    }

    // Update HP bar
    this.updateHpBar();

    // Update depth based on bottom of visual (for correct overlap with trees/towers)
    this.updateDepth();

    // Sync ghost sprite position
    this.syncGhostSprite();
  }

  /**
   * Sync ghost sprite with main sprite position
   */
  /** Bottom of monster visual for depth sorting (lower on screen = in front). Uses container.y + body height/2 because GetBottom(body) returns local coords for container children. */
  protected getDepthY(): number {
    const h = (this.body as Phaser.GameObjects.Components.ComputedSize).displayHeight ?? this.stats.size;
    return this.container.y + h / 2;
  }

  private syncGhostSprite(): void {
    if (!this.ghostSprite) return;
    this.ghostSprite.setPosition(this.container.x, this.container.y);
    
    // Also sync flip state for image-based ghost sprites
    if (this.useImage && this.ghostSprite instanceof Phaser.GameObjects.Image) {
      this.ghostSprite.setFlipX(this.lastMoveDirection < 0);
    }
  }

  /**
   * Update running animation for image-based monsters
   */
  private updateAnimation(delta: number): void {
    if (!this.useImage) return;

    // Per-type interval (e.g. swarm 75ms, others 200ms); scale by slow multiplier
    const baseMs = this.stats.animationIntervalMs ?? this.BASE_ANIMATION_INTERVAL_MS;
    const animationInterval = baseMs / this.slowMultiplier;

    this.animationTimer += delta;

    if (this.animationTimer >= animationInterval) {
      this.animationTimer = 0;
      
      // Advance to next frame (cycle through all frames: brute 4, shielded 3, crawler/runner 2)
      this.animationFrame = (this.animationFrame + 1) % this.animationFrameCount;
      
      // Update texture
      const frameKey = `monster-${this.monsterType}-${this.animationFrame + 1}`;
      if (this.scene.textures.exists(frameKey)) {
        (this.body as Phaser.GameObjects.Image).setTexture(frameKey);
        
        // Also update ghost sprite if it's an image
        if (this.ghostSprite instanceof Phaser.GameObjects.Image) {
          this.ghostSprite.setTexture(frameKey);
        }
      }
    }

    // Flip sprite based on movement direction
    // Images face right by default, flip when moving left
    (this.body as Phaser.GameObjects.Image).setFlipX(this.lastMoveDirection < 0);
  }

  private processBurn(delta: number): void {
    if (this.burnDamage > 0 && Date.now() < this.burnEndTime) {
      const burnTick = this.burnDamage * (delta / 1000);
      this.hp -= burnTick;

      // Visual burn effect
      if (Math.random() < 0.1) {
        this.showBurnParticle();
      }

      if (this.hp <= 0) {
        this.die();
      }
    } else {
      this.burnDamage = 0;
      this.clearBurnVisual();
    }
  }

  private clearBurnVisual(): void {
    if (this.destroyed) return;
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      (this.body as Phaser.GameObjects.Image).clearTint();
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      (this.body as Phaser.GameObjects.Ellipse).setStrokeStyle(2, 0x000000);
    } else if (this.body instanceof Phaser.GameObjects.Arc) {
      (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, 0xffffff);
    }
  }

  private showBurnParticle(): void {
    const particle = this.scene.add.circle(
      this._x + Phaser.Math.Between(-10, 10),
      this._y + Phaser.Math.Between(-10, 10),
      3,
      0xff4500
    );
    particle.setDepth(100);

    this.scene.tweens.add({
      targets: particle,
      y: particle.y - 20,
      alpha: 0,
      scale: 0,
      duration: 400,
      onComplete: () => particle.destroy(),
    });
  }

  private updateHpBar(): void {
    if (this.destroyed || !this.hpBar) return;

    const hpPercent = Math.max(0, this.hp / this.maxHp);
    const maxWidth = this.stats.size;
    this.hpBar.setSize(maxWidth * hpPercent, 4);

    // Color based on HP
    if (hpPercent > 0.6) {
      this.hpBar.setFillStyle(COLORS.SECONDARY);
    } else if (hpPercent > 0.3) {
      this.hpBar.setFillStyle(0xffaa00);
    } else {
      this.hpBar.setFillStyle(COLORS.DANGER);
    }
  }

  /**
   * Take damage with resistance calculation
   * Returns { damageDealt, killed } for tower XP tracking
   */
  takeDamage(amount: number, source?: TowerType): { damageDealt: number; killed: boolean } {
    if (this.destroyed) return { damageDealt: 0, killed: false };

    let actualDamage = amount;

    // Apply resistances
    if (source === 'arrow' && this.stats.arrowResist) {
      actualDamage *= (1 - this.stats.arrowResist);
    }

    if ((source === 'cannon' || source === 'mortar') && this.stats.splashResist) {
      actualDamage *= (1 - this.stats.splashResist);
    }

    this.hp -= actualDamage;

    // Flash effect + bounce/squash animation
    if (this.useImage) {
      // For images, use tint for flash effect
      (this.body as Phaser.GameObjects.Image).setTint(0xffffff);
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      this.body.setFillStyle(0xffffff);
    } else {
      (this.body as Phaser.GameObjects.Arc).setFillStyle(0xffffff);
    }

    // Bounce/squash effect - scale up briefly then back
    const bounceScale = 1.15 + (actualDamage / this.maxHp) * 0.2; // Bigger hits = bigger bounce
    this.scene.tweens.add({
      targets: this.body,
      scaleX: this.useImage ? 0.25 * bounceScale : bounceScale,
      scaleY: this.useImage ? 0.25 * 0.85 : 0.85,
      duration: 50,
      yoyo: true,
      ease: 'Quad.easeOut',
    });

    this.scene.time.delayedCall(50, () => {
      if (!this.destroyed) {
        if (this.useImage) {
          // Clear tint for images
          (this.body as Phaser.GameObjects.Image).clearTint();
        } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
          this.body.setFillStyle(this.stats.color);
        } else {
          (this.body as Phaser.GameObjects.Arc).setFillStyle(this.stats.color);
        }
      }
    });

    // Check death
    const killed = this.hp <= 0;
    if (killed) {
      this.die();
    }
    
    return { damageDealt: actualDamage, killed };
  }

  /**
   * Apply slow effect
   */
  applySlow(multiplier: number, duration: number): void {
    // Phased monsters are immune to slow
    if (this.stats.slowImmune) return;

    this.slowMultiplier = multiplier;
    this.slowEndTime = Date.now() + duration;

    // Visual feedback - blue tint for images, stroke for shapes
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      (this.body as Phaser.GameObjects.Image).setTint(COLORS.TOWER_ICE);
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      this.body.setStrokeStyle(2, COLORS.TOWER_ICE);
    } else if (this.body instanceof Phaser.GameObjects.Arc) {
      (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, COLORS.TOWER_ICE);
    }

    this.scene.time.delayedCall(duration, () => {
      if (!this.destroyed) {
        if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
          (this.body as Phaser.GameObjects.Image).clearTint();
        } else {
          const strokeColor = this.stats.movementType === 'air' ? 0xffffff : 0x000000;
          if (this.body instanceof Phaser.GameObjects.Ellipse) {
            this.body.setStrokeStyle(2, strokeColor);
          } else if (this.body instanceof Phaser.GameObjects.Arc) {
            (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, strokeColor);
          }
        }
      }
    });
  }

  /**
   * Apply burn DoT effect
   */
  applyBurn(damagePerSecond: number, duration: number): void {
    this.burnDamage = damagePerSecond;
    this.burnEndTime = Date.now() + duration;

    // Visual feedback - orange tint for images, stroke for shapes
    if (this.useImage && this.body instanceof Phaser.GameObjects.Image) {
      (this.body as Phaser.GameObjects.Image).setTint(0xff6600);
    } else if (this.body instanceof Phaser.GameObjects.Ellipse) {
      this.body.setStrokeStyle(2, 0xff4500);
    } else if (this.body instanceof Phaser.GameObjects.Arc) {
      (this.body as Phaser.GameObjects.Arc).setStrokeStyle(2, 0xff4500);
    }
  }

  /**
   * Monster died (from damage)
   */
  private die(): void {
    if (this.destroyed) return;

    console.log(`[Monster] ${this.monsterType} died, reward: ${this.stats.reward}`);

    // Play death sound at 30% volume
    AudioRouter.playMonsterDeath();

    // Death effect
    this.showDeathEffect();

    // Callback
    this.onDeath?.(this.stats.reward);

    // Destroy
    this.destroy();
  }

  /**
   * Monster reached the base
   */
  private reachedBase(): void {
    if (this.destroyed) return;

    console.log(`[Monster] ${this.monsterType} reached base, damage: ${this.stats.damage}`);

    // Callback
    this.onReachedBase?.();

    // Destroy without death effect
    this.destroy();
  }

  private showDeathEffect(): void {
    const x = this._x;
    const y = this._y;
    const color = this.stats.color;

    // Explosion particles
    for (let i = 0; i < 6; i++) {
      const particle = this.scene.add.circle(x, y, 4, color);
      particle.setDepth(100);

      const angle = (i / 6) * Math.PI * 2;
      const dist = 30;

      this.scene.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist,
        alpha: 0,
        scale: 0,
        duration: 300,
        onComplete: () => particle.destroy(),
      });
    }

    // Note: No reward text - gems/coins must be picked up manually via drops
  }

  // ============================================
  // Getters
  // ============================================

  getHp(): number { return this.hp; }
  getMaxHp(): number { return this.maxHp; }
  getPathProgress(): number { return this.pathProgress; }
  getDamage(): number { return this.stats.damage; }
  getReward(): number { return this.stats.reward; }
  getSpeed(): number { return this.stats.speed * this.slowMultiplier; }
  getMovementType(): MonsterMovementType { return this.stats.movementType; }
  getTier(): number { return this.stats.tier; }
  isSlowed(): boolean { return this.slowMultiplier < 1; }
  isBurning(): boolean { return this.burnDamage > 0 && Date.now() < this.burnEndTime; }

  static getStats(monsterType: MonsterType): MonsterStats {
    return { ...MONSTER_STATS[monsterType] };
  }

  static getAllTypes(): MonsterType[] {
    return Object.keys(MONSTER_STATS) as MonsterType[];
  }

  static getTypesByTier(tier: number): MonsterType[] {
    return (Object.entries(MONSTER_STATS) as [MonsterType, MonsterStats][])
      .filter(([_, stats]) => stats.tier === tier)
      .map(([type, _]) => type);
  }

  static getGroundTypes(): MonsterType[] {
    return (Object.entries(MONSTER_STATS) as [MonsterType, MonsterStats][])
      .filter(([_, stats]) => stats.movementType === 'ground')
      .map(([type, _]) => type);
  }

  static getAirTypes(): MonsterType[] {
    return (Object.entries(MONSTER_STATS) as [MonsterType, MonsterStats][])
      .filter(([_, stats]) => stats.movementType === 'air')
      .map(([type, _]) => type);
  }

  /**
   * Clean up ghost sprite on destroy
   */
  destroy(): void {
    if (this.ghostSprite) {
      this.ghostSprite.destroy();
      this.ghostSprite = null;
    }
    super.destroy();
  }
}
